// Render-pipeline constants. Per spec/tech-arch.md §3.2 + spec/operating-
// contract.md §3.4 (Tech Arch lockstep). Co-located with `html.ts` /
// `document-shell.ts` so the entire render-time emission contract lives
// in one module group.
//
// **`BASE_URL` (P-037.26 D-134).** Absolute origin prefix applied to
// every relative asset URL in the rendered email HTML. The default
// matches today's Firebase Hosting deploy
// (`https://ypo-email-builder.web.app`). `VITE_BASE_URL` overrides at
// build time — used post-P-044 when the chapter subdomain wires up via
// DNS + Hosting custom-domain config (Banks-confirmed at the P-037.26
// kickoff per OC §6.4 over a hardcode-only alternative).
//
// **Why absolute URLs in email output.** The rendered HTML lands in
// three places:
//
// 1. **In-app preview iframe** (same-origin srcdoc) — relative paths
//    work because the iframe's base URL inherits the parent's origin.
// 2. **View-in-Tab** (`blob:` URL) — relative paths fail. A blob URL's
//    document has its own base URL (`blob:<origin>/<uuid>`); relative
//    paths inside resolve against that base, but image clients in the
//    new tab can't fetch from a `blob:` origin. Banks's P-037.26
//    walkthrough showed broken-image icons in the View-in-Tab path.
// 3. **Constant Contact (V1 launch / P-044)** — chapter staff pastes
//    the rendered HTML into CC's Custom Code editor. CC's domain
//    serves the email to recipients; relative paths can't resolve to
//    our hosting. The same broken-image failure mode as #2, but for
//    every recipient's inbox.
//
// **Why a `toAbsoluteUrl` helper (not raw concatenation at each
// emission site).** The repo convention is: every relative asset path
// emits with a leading `/` (e.g., `/ypo-s7.png`, `/flags/al.png`,
// `/ypo-s7-email-header-*.png`). The helper's rule is the same as that
// convention, in reverse — **prefix only when the path starts with `/`
// AND is NOT a protocol-relative URL** (`//host/path`). Everything else
// passes through. That covers:
//
// 1. **Already-absolute URLs** (`http://...`, `https://...`) — Firebase
//    Storage chapter uploads (`https://firebasestorage.googleapis.com/
//    ...` per D-119) and any external link with an explicit scheme.
//    Double-prefixing would produce `https://ypo-email-builder.web.app
//    https://...` — a broken URL.
// 2. **Protocol-relative URLs** (`//cdn.example.com/x.png`) — the
//    leading `//` is an explicit absolute marker; the browser resolves
//    against the page's protocol. Skipping the prefix preserves that
//    semantic. Excluded explicitly via the `!startsWith('//')` guard
//    paired with the `startsWith('/')` rule.
// 3. **Constant Contact variables** (`[[unsubscribe]]`, etc.) —
//    CC substitutes these at send time. They are NOT URLs at render
//    time; prefixing would smuggle the BASE_URL into the substituted
//    output.
// 4. **Same-document fragment identifiers** (`#section`) — resolve
//    against the current document URL; prefixing would break the
//    anchor.
// 5. **Non-http schemes** (`mailto:foo@bar`, `tel:+15551234567`,
//    `data:image/png;base64,...`) — explicit schemes; same logic as #1.
//
// The narrow rule ("starts with `/` AND not `//`") catches case 1+2+3+4+5
// in one pass without enumerating each non-matching scheme. Future
// repo paths that should NOT be prefixed (e.g., a hypothetical `~foo`
// scheme) automatically fall under "pass through" because they don't
// start with `/`.
//
// **Security walk (D-134 §security).** The helper is a pure string
// transform — input + concat or input pass-through. The kernel's
// `html\`\`` interpolation still HTML-escapes the resulting attribute
// value at the emission site, so a maliciously-crafted path like
// `/" onerror=alert(1)` is escaped to `/&quot; onerror=alert(1)` and
// can't break out of the attribute. BASE_URL itself is a build-time
// literal (no runtime user input), so it can't carry attacker payload.
// CC variables pass through to CC; CC's substitution layer is the
// trust boundary for those.

const FALLBACK_BASE_URL = 'https://ypo-email-builder.web.app'

/**
 * Absolute origin prefix for relative asset URLs in rendered email
 * HTML. Defaults to today's Firebase Hosting deploy origin; overridden
 * at build time via `VITE_BASE_URL` (Vite's `import.meta.env` surface)
 * when the chapter subdomain takes over post-P-044.
 *
 * **Optional-chain on `import.meta.env`** — under Vite (dev / build /
 * Vitest), `import.meta.env` is always defined. Under Playwright's
 * Node runtime (`playwright test`), the source loads directly without
 * Vite's transform and `import.meta.env` itself is `undefined`. The
 * `?.` makes the lookup safely yield `undefined` in that path so the
 * `??` falls through to `FALLBACK_BASE_URL`. Carry-forward fix from
 * P-037.27's pre-flight — prior P-037.26 close broke CI on `main` +
 * the visual-baselines `workflow_dispatch` with a `TypeError: Cannot
 * read properties of undefined (reading 'VITE_BASE_URL')` here.
 */
export const BASE_URL: string = import.meta.env?.VITE_BASE_URL ?? FALLBACK_BASE_URL

/**
 * Convert a render-time `src=` / `href=` value to its absolute form.
 *
 * Prefix rule: prefix ONLY when `path` starts with `/` and is NOT
 * protocol-relative (`//host/path`). Every other shape (absolute URL,
 * CC variable, fragment, mailto:, tel:, data:, …) passes through
 * unchanged. See module-header for the full rationale.
 *
 * **Defensive non-string handling.** The typed contract requires
 * `string`, but some test surfaces deliberately bypass the contract
 * (e.g. `@ts-expect-error` invocations that confirm a TS-only
 * paired-required union). The pre-D-134 emission sites silently
 * interpolated `undefined` as the string `"undefined"` in the HTML;
 * preserving that behavior here (pass through whatever non-string
 * value the caller hands us, coerced to its String() form) keeps
 * those tests valid + closes the runtime-TypeError seam. Production
 * code never hits this path.
 *
 * Pure — no I/O, no side effects, no allocations beyond the result
 * string. Safe to call at every emission site.
 */
export function toAbsoluteUrl(path: string): string {
  if (typeof path !== 'string') return String(path)
  if (!path.startsWith('/')) return path
  if (path.startsWith('//')) return path
  return `${BASE_URL}${path}`
}
